import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { AAROH_ALBUM } from "../../data/album.js";
import { AlbumImg } from "./media.jsx";
import { Btn, EASE_IN_OUT, GoLink, Rise } from "./pgkit.jsx";
import "./programmes.css";

const DOORS = [
  {
    slug: "aaroh",
    title: "AAROH",
    role: "GRID organised",
    album: AAROH_ALBUM.find((item) => item.id === "M054"),
    focus: "50% 55%",
    summary: "A two-day student innovation festival at IIIT Delhi on 1 and 2 August 2026, run with Tachyon India and CiPD. Teams built across four tracks and 13 were recognised.",
    facts: [["363", "young innovators"], ["13", "teams recognised"]],
    cta: "Read the AAROH recap",
  },
  {
    slug: "aurora",
    title: "Aurora",
    role: "GRID organised",
    image: "/images/grid-photo-10-p8.jpg",
    focus: "50% 30%",
    alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his prize laptop",
    summary: "A global online hackathon. Teams moved from an idea round to prototypes and live pitches, and the Top 20 entered incubation.",
    facts: [["1,500", "participants"], ["27+", "countries"]],
    cta: "See Aurora's results",
  },
  {
    slug: "metamorphosis",
    title: "Metamorphosis",
    role: "GRID was the outreach partner",
    image: "/images/metamorphosis/album/meta-745-cohort-group-photo-1600.webp",
    focus: "50% 50%",
    alt: "Metamorphosis students packing a lecture hall for a group photo at IIT Delhi",
    summary: "Organised by Tale of Humankind Foundation and IIT Delhi's outreach office, with GRID as the outreach partner. One week at IIT Delhi for students from 18 states.",
    facts: [["150", "students"], ["36", "products"]],
    cta: "How we supported it",
  },
];

const ROWS = [
  {
    name: "AAROH",
    to: "/programmes/aaroh",
    led: "Project GRID, with Tachyon India and CiPD at IIIT Delhi",
    role: "Organiser",
    where: "IIIT Delhi",
    when: "1–2 August 2026",
    people: "363 young innovators",
    site: ["aarohindia.com", "https://www.aarohindia.com/"],
  },
  {
    name: "Aurora",
    to: "/programmes/aurora",
    led: "Project GRID",
    role: "Organiser",
    where: "Online, 27+ countries",
    when: "From January 2026",
    people: "1,500 participants",
    site: ["aurora.projectgrid.org", "https://aurora.projectgrid.org/"],
  },
  {
    name: "Metamorphosis",
    to: "/programmes/metamorphosis",
    led: "Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives",
    role: "Outreach partner",
    where: "IIT Delhi",
    when: "May 2026",
    people: "150 students",
    site: ["metamorphosis.taleofhumankind.org", "https://metamorphosis.taleofhumankind.org/"],
  },
];

function Door({ door, index, open, onOpen }) {
  const reduceMotion = useReducedMotion();
  return (
    <Link
      className={`pg-door ${open ? "is-open" : ""}`}
      to={`/programmes/${door.slug}`}
      onMouseEnter={onOpen}
      onFocus={onOpen}
    >
      <motion.div
        className="pg-door__media"
        initial={reduceMotion ? false : { clipPath: "inset(0% 50% 0% 50% round 28px)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.15, delay: 0.15 + index * 0.12, ease: EASE_IN_OUT }}
      >
        {door.album ? (
          <AlbumImg photo={door.album} sizes="(max-width: 899px) 100vw, 60vw" priority={index === 0} style={{ objectPosition: door.focus }} />
        ) : (
          <Photo src={door.image} alt={door.alt} sizes="(max-width: 899px) 100vw, 60vw" style={{ objectPosition: door.focus }} />
        )}
      </motion.div>
      <div className="pg-door__plate">
        <span className="pg-door__role">{door.role}</span>
        <h2>{door.title}</h2>
        <div className="pg-door__more">
          <div>
            <p>{door.summary}</p>
            <dl>
              {door.facts.map(([value, label]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}
            </dl>
            <span className="pg-door__cta">{door.cta} <ArrowRight weight="bold" aria-hidden="true" /></span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function Doors() {
  const [open, setOpen] = useState(0);
  const stacked = useMediaQuery("(max-width: 899px)");
  return (
    <section className="pg-doors" aria-label="The three programmes">
      {DOORS.map((door, index) => (
        <Door key={door.slug} door={door} index={index} open={stacked || open === index} onOpen={() => setOpen(index)} />
      ))}
    </section>
  );
}

function CompareTable() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });
  return (
    <section className="pg-compare" aria-labelledby="pg-compare-title" id="compare">
      <header className="pg-compare__head">
        <MaskWords id="pg-compare-title" text="Side by side." />
        <p>Who led each programme, what GRID did, and how many people took part.</p>
      </header>
      <div className={`pg-table-wrap ${inView ? "is-in" : ""}`} ref={ref}>
        <table className="pg-table">
          <thead>
            <tr>
              <th scope="col">Programme</th>
              <th scope="col">Who led it</th>
              <th scope="col">GRID&apos;s role</th>
              <th scope="col">Where</th>
              <th scope="col">When</th>
              <th scope="col">Participants</th>
              <th scope="col"><span className="sr-only">Links</span></th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.name}>
                <th scope="row">{row.name}</th>
                <td data-label="Who led it">{row.led}</td>
                <td data-label="GRID's role"><span className={`pg-role ${row.role.startsWith("Outreach") ? "pg-role--partner" : ""}`}>{row.role}</span></td>
                <td data-label="Where">{row.where}</td>
                <td data-label="When">{row.when}</td>
                <td data-label="Participants">{row.people}</td>
                <td className="pg-table__links">
                  <Link to={row.to}>Read more<span className="sr-only"> about {row.name}</span> <ArrowRight weight="bold" aria-hidden="true" /></Link>
                  <a href={row.site[1]} target="_blank" rel="noreferrer">{row.site[0]} <ArrowUpRight weight="bold" aria-hidden="true" /></a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <aside className="pg-compare__note" aria-label="In total">
        <span className="gh-mono">In total</span>
        <p>2,013 participants across AAROH, Aurora and Metamorphosis.</p>
        <GoLink to="/impact">See our impact</GoLink>
      </aside>
    </section>
  );
}

export default function ProgrammesHubPage() {
  return (
    <PageFrame className="rd-page pg-page pg-hub">
      <Seo
        title="Programmes and Collaborations"
        path="/programmes"
        description="Explore Project GRID's own programme work and the youth initiatives it has supported through clearly labelled collaborations."
      />
      <main>
        <section className="pg-hub__intro" aria-labelledby="pg-hub-title">
          <Rise as="p" className="gh-mono pg-kicker" onMount>Programmes · 2026</Rise>
          <MaskWords as="h1" id="pg-hub-title" text="What we ran, and what we helped with." onMount delay={0.05} />
          <div className="pg-hub__row">
            <Rise as="p" onMount delay={0.35}>
              GRID organised AAROH and Aurora. For Metamorphosis, organised by Tale of Humankind Foundation and IIT Delhi, we were the outreach partner. Every page below says who led it and what happened.
            </Rise>
            <Rise className="gh-actions" onMount delay={0.45}>
              <Btn to="/programmes/upcoming">See upcoming events</Btn>
              <Btn href="#compare" variant="ghost">Compare the three</Btn>
            </Rise>
          </div>
        </section>
        <Doors />
        <CompareTable />
        <section className="pg-hub__next" aria-labelledby="pg-hub-next-title">
          <div className="pg-hub__next-inner">
          <h2 id="pg-hub-next-title">Want to be in the next one?</h2>
          <div>
            <p>Confirmed events go on the upcoming board with dates, who can apply and any fee. Schools can also ask us to bring an activity to their students.</p>
            <div className="gh-actions">
              <Btn to="/programmes/upcoming">Upcoming events</Btn>
              <Btn to="/for-schools" variant="ghost">For schools</Btn>
            </div>
          </div>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
